import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { label: "About", target: "#about" },
  { label: "Services", target: "#services" },
  { label: "Why Us", target: "#why" },
  { label: "Contact", target: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    const el = document.querySelector(target);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20, duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-[100] transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-[#12121a]/70 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button onClick={() => go("#hero")} data-testid="nav-logo-button" className="cursor-pointer">
          <Logo />
        </button>

        <div className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.target}
              onClick={() => go(l.target)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className="font-body text-sm text-[#9ba1a6] hover:text-[#e8d9a8] tracking-wide transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#contact")}
            data-testid="nav-cta-button"
            className="group relative font-body text-sm tracking-wide px-6 py-2.5 rounded-full border border-[#c9a84c]/40 text-[#e8d9a8] hover:text-[#0a0a0f] overflow-hidden transition-colors duration-300"
          >
            <span className="relative z-10">Book a Consultation</span>
            <span className="absolute inset-0 bg-[#c9a84c] translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]" />
          </button>
        </div>

        <button
          className="md:hidden text-[#e8d9a8] p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#12121a]/95 backdrop-blur-xl border-b border-white/5"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l) => (
                <button
                  key={l.target}
                  onClick={() => go(l.target)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(" ", "-")}`}
                  className="text-left font-display text-2xl text-[#f8f9fa]"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                data-testid="nav-mobile-cta"
                className="mt-2 w-full text-center font-body text-sm tracking-wide px-6 py-3.5 rounded-full bg-[#c9a84c] text-[#0a0a0f] font-medium"
              >
                Book a Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
